import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import { Interaction } from "chart.js";
import { getChartThemeColors, observeThemeChange } from "utils/chartTheme";
import { chartPoints, pointUnit } from "lib/blogChartPoints.mjs";

// The Blog Hub's pageviews line (blog page and post page). One series, so no
// legend: the panel title names it. Hovering anywhere over the plot shows a
// crosshair on the nearest point with its number; the range buttons pick the
// window, and dragging across the plot with a mouse zooms into that stretch
// ("Reset zoom" goes back). The summary line above the chart follows whatever
// window is showing, so the numbers never live in the tooltip alone.
//
// series: [["2026-10-01", 123], ...], oldest first. unit "day" is a daily
// series, drawn a point per day, week or month by how much is showing (see
// lib/blogChartPoints.mjs); unit "month" is already one row per month.
//
// mode "level" (a profile's followers) charts a reading rather than a daily
// amount: always a point per day, null days are gaps bridged by a faint
// dashed line, the axis doesn't start at zero, and the summary is the change
// from the first reading to the last.

const FALLBACK_COLOR = "#0A7EA4"; // --chart-1

// Hover snaps to the closest day that has a reading. Chart.js's own "index"
// and "nearest" modes land on the empty day under the cursor and show no
// tooltip, which on a sparse follower line is most of the plot.
Interaction.modes.nearestReading = (chart, event) => {
  const meta = chart.getDatasetMeta(0);
  let best = null;
  meta.data.forEach((element, index) => {
    if (element.skip) return;
    const distance = Math.abs(element.x - event.x);
    if (!best || distance < best.distance) best = { element, index, distance };
  });
  return best
    ? [{ element: best.element, datasetIndex: 0, index: best.index }]
    : [];
};

function lineColor() {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--chart-1")
    .trim();
  return value || FALLBACK_COLOR;
}

function rgba(hex, alpha) {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function compactNumber(value) {
  const n = Number(value);
  if (Math.abs(n) >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${+(n / 1e3).toFixed(1)}K`;
  return `${n}`;
}

const number = new Intl.NumberFormat();

function parseDay(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function shortDay(iso) {
  return parseDay(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function monthName(iso) {
  return parseDay(iso).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function shortMonth(iso) {
  return parseDay(iso).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

function longDayNoWeekday(iso) {
  return parseDay(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function longDay(iso) {
  return parseDay(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// A thin vertical rule through the hovered day, and the drag selection while
// the user is brushing a range.
const overlayPlugin = {
  id: "blogChartOverlay",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    const active = chart.tooltip && chart.tooltip.getActiveElements();
    if (active && active.length && !opts.brush) {
      const x = active[0].element.x;
      ctx.save();
      ctx.strokeStyle = opts.ruleColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    }
    if (opts.brush) {
      const left = Math.max(
        Math.min(opts.brush.from, opts.brush.to),
        chartArea.left
      );
      const right = Math.min(
        Math.max(opts.brush.from, opts.brush.to),
        chartArea.right
      );
      ctx.save();
      ctx.fillStyle = opts.brushColor;
      ctx.fillRect(
        left,
        chartArea.top,
        right - left,
        chartArea.bottom - chartArea.top
      );
      ctx.restore();
    }
  },
};

export default class extends Controller {
  static values = {
    series: Array,
    label: String,
    unit: { type: String, default: "day" },
    initial: Number,
    mode: { type: String, default: "sum" },
  };
  static targets = ["canvas", "range", "reset", "summary"];

  connect() {
    const last = this.seriesValue.length - 1;
    const shown = this.initialValue || this.seriesValue.length;
    this.window = [Math.max(0, last - shown + 1), last];
    this.draw();
    this.stopObserving = observeThemeChange(() => this.draw());
    this.onDown = this.pointerDown.bind(this);
    this.onMove = this.pointerMove.bind(this);
    this.onUp = this.pointerUp.bind(this);
    this.canvasTarget.addEventListener("pointerdown", this.onDown);
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
  }

  disconnect() {
    if (this.stopObserving) this.stopObserving();
    this.canvasTarget.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    if (this.chart) this.chart.destroy();
  }

  // Actions

  range(event) {
    const days = Number(event.currentTarget.dataset.days);
    const last = this.seriesValue.length - 1;
    this.setWindow(Math.max(0, last - days + 1), last);
    this.rangeTargets.forEach((button) => {
      const on = button === event.currentTarget;
      button.classList.toggle("is-active", on);
      button.setAttribute("aria-pressed", on);
    });
    this.resetTarget.hidden = true;
  }

  resetZoom() {
    const active = this.rangeTargets.find((b) =>
      b.classList.contains("is-active")
    );
    const days = active ? Number(active.dataset.days) : this.seriesValue.length;
    const last = this.seriesValue.length - 1;
    this.setWindow(Math.max(0, last - days + 1), last);
    this.resetTarget.hidden = true;
  }

  // Brushing (mouse only: on a touch screen a drag scrolls the page)

  pointerDown(event) {
    if (event.pointerType !== "mouse" || event.button !== 0 || !this.chart)
      return;
    const x = this.relativeX(event);
    const area = this.chart.chartArea;
    if (x < area.left || x > area.right) return;
    this.brush = { from: x, to: x };
  }

  pointerMove(event) {
    if (!this.brush) return;
    this.brush.to = this.relativeX(event);
    this.chart.options.plugins.blogChartOverlay.brush = this.brush;
    this.chart.update("none");
  }

  pointerUp() {
    if (!this.brush) return;
    const { from, to } = this.brush;
    this.brush = null;
    this.chart.options.plugins.blogChartOverlay.brush = null;
    const scale = this.chart.scales.x;
    const a = this.indexAt(scale, Math.min(from, to));
    const b = this.indexAt(scale, Math.max(from, to));
    // A click, or a sliver under three points, is not a zoom.
    if (Math.abs(to - from) < 8 || b - a < 2) {
      this.chart.update("none");
      return;
    }
    const points = this.points;
    this.setWindow(
      this.window[0] + points[a].from,
      this.window[0] + points[b].to
    );
    this.resetTarget.hidden = false;
  }

  // Private

  relativeX(event) {
    const rect = this.canvasTarget.getBoundingClientRect();
    return event.clientX - rect.left;
  }

  indexAt(scale, x) {
    const i = Math.round(scale.getValueForPixel(x));
    return Math.min(Math.max(i, 0), this.points.length - 1);
  }

  setWindow(from, to) {
    this.window = [from, to];
    this.points = this.bucketed();
    this.chart.data.labels = this.points.map((p) => p.label);
    const { solid, partial } = this.lineData();
    this.chart.data.datasets[0].data = solid;
    this.chart.data.datasets[1].data = partial;
    this.chart.update("none");
    this.summarize();
  }

  visible() {
    return this.seriesValue.slice(this.window[0], this.window[1] + 1);
  }

  get level() {
    return this.modeValue === "level";
  }

  bucketed() {
    // A reading can't be summed into a week, so a level line stays daily.
    this.currentUnit = this.level
      ? "day"
      : pointUnit(this.window[1] - this.window[0] + 1, this.unitValue);
    return chartPoints(this.visible(), this.currentUnit, this.unitValue);
  }

  // A week or month the window only partly covers is drawn dashed (its own
  // dataset: Chart.js 3.0 has no per-segment styles), so its smaller total
  // doesn't read as a drop in traffic. The solid line stops short of it.
  lineData() {
    const points = this.points;
    // Level: solid between readings on consecutive days, and the same values
    // again for the dashed bridge across the gaps.
    if (this.level) {
      const values = points.map((p) => p.views);
      return { solid: values, partial: values };
    }
    const partialAt = (i) => points[i] && !points[i].complete;
    return {
      solid: points.map((p) => (p.complete ? p.views : null)),
      partial: points.map((p, i) =>
        partialAt(i) || partialAt(i - 1) || partialAt(i + 1) ? p.views : null
      ),
    };
  }

  // How a point reads in the tooltip and the summary.
  // A month the window only partly covers reads as its days.
  pointName(point) {
    if (this.currentUnit === "day") return shortDay(point.first);
    if (this.currentUnit === "month" && point.complete)
      return monthName(point.first);
    return `${shortDay(point.first)} – ${longDayNoWeekday(point.last)}`;
  }

  // Dates in the summary: with the year once the window spans more than one.
  edgeDay(iso) {
    if (this.unitValue === "month") return monthName(iso);
    const slice = this.visible();
    const years =
      slice[0][0].slice(0, 4) !== slice[slice.length - 1][0].slice(0, 4);
    return years ? longDayNoWeekday(iso) : shortDay(iso);
  }

  summarize() {
    if (!this.hasSummaryTarget) return;
    const slice = this.visible();
    const points = this.points;
    if (!slice.length || !points.length) return;
    if (this.level) {
      this.summaryTarget.textContent = this.levelSummary(slice);
      return;
    }
    const total = slice.reduce((sum, [, views]) => sum + views, 0);
    const peak = points.reduce((best, p) => (p.views > best.views ? p : best));
    const from = this.edgeDay(slice[0][0]);
    const to = this.edgeDay(slice[slice.length - 1][0]);
    // "1,234 pageviews · Jul 27 → Oct 24 · Peak Oct 11: 680"
    this.summaryTarget.textContent =
      `${number.format(total)} ${this.labelValue.toLowerCase()} · ` +
      `${from} → ${to} · Peak ${this.pointName(peak)}: ${number.format(
        peak.views
      )}`;
  }

  // "Down 312 · Oct 14 → Oct 21", the same as level_chart_summary.
  levelSummary(slice) {
    const readings = slice.filter(([, n]) => n !== null && n !== undefined);
    if (!readings.length) return "No readings";
    const [firstDay, first] = readings[0];
    const [lastDay, last] = readings[readings.length - 1];
    if (readings.length < 2)
      return `${number.format(last)} · ${shortDay(lastDay)}`;
    const change = last - first;
    const direction =
      change > 0
        ? `Up ${number.format(change)}`
        : change < 0
        ? `Down ${number.format(-change)}`
        : "No change";
    return `${direction} · ${shortDay(firstDay)} → ${shortDay(lastDay)}`;
  }

  datasets(color, theme, label) {
    const { solid, partial } = this.lineData();
    const line = {
      label,
      borderColor: color,
      borderWidth: 2,
      tension: 0.25,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: theme.tooltipBackgroundColor,
      pointHoverBackgroundColor: color,
    };
    if (this.level)
      return [
        {
          ...line,
          data: solid,
          backgroundColor: color,
          fill: false,
          spanGaps: false,
          pointRadius: 2.5,
        },
        {
          ...line,
          data: partial,
          borderColor: rgba(color, 0.45),
          borderWidth: 1.5,
          borderDash: [4, 4],
          fill: false,
          spanGaps: true,
          pointHoverRadius: 0,
        },
      ];
    return [
      { ...line, data: solid, backgroundColor: rgba(color, 0.12), fill: true },
      {
        ...line,
        data: partial,
        borderDash: [4, 4],
        backgroundColor: rgba(color, 0.06),
        fill: true,
      },
    ];
  }

  draw() {
    if (!this.hasCanvasTarget) return;
    if (this.chart) this.chart.destroy();
    const theme = getChartThemeColors();
    const color = lineColor();
    this.points = this.bucketed();
    const label = this.labelValue;
    const controller = this;

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: this.points.map((p) => p.label),
        datasets: this.datasets(color, theme, label),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: this.level ? "nearestReading" : "index",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          blogChartOverlay: {
            ruleColor: theme.tooltipBorderColor,
            brushColor: rgba(color, 0.14),
            brush: null,
          },
          tooltip: {
            displayColors: false,
            backgroundColor: theme.tooltipBackgroundColor,
            titleColor: theme.tooltipTitleColor,
            bodyColor: theme.tooltipBodyColor,
            borderColor: theme.tooltipBorderColor,
            borderWidth: 1,
            padding: 10,
            bodyFont: { size: 14, weight: "600" },
            titleFont: { size: 12, weight: "400" },
            // One line per point: the dashed line where it is partial.
            filter: (item) => {
              if (controller.level) return item.datasetIndex === 0;
              const point = controller.points[item.dataIndex];
              return (item.datasetIndex === 1) === !point.complete;
            },
            callbacks: {
              title: (items) => {
                const point = controller.points[items[0].dataIndex];
                return controller.currentUnit === "day"
                  ? longDay(point.first)
                  : controller.pointName(point);
              },
              label: (item) =>
                `${number.format(item.parsed.y)} ${label.toLowerCase()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: theme.tickColor,
              font: { size: 12 },
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
              callback(value) {
                const iso = this.getLabelForValue(value);
                return controller.currentUnit === "day"
                  ? shortDay(iso)
                  : shortMonth(iso);
              },
            },
          },
          y: {
            beginAtZero: !this.level,
            grid: { color: theme.gridColor, drawBorder: false },
            ticks: {
              color: theme.tickColor,
              font: { size: 12 },
              maxTicksLimit: 5,
              callback: compactNumber,
            },
          },
        },
      },
      plugins: [overlayPlugin],
    });
    this.summarize();
  }
}
